import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Spinner, Alert } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./AddPurchase.css";
import api from "../services/api";

const AddOrder = () => {
  const [formData, setFormData] = useState({
    customer_id: "",
    date: new Date().toISOString().split('T')[0],
    productSearch: "",
    sellingRate: "",
    qty: "",
    gst_percentage: 18,
    selectedProduct: null
  });

  const [orderItems, setOrderItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  // Customer modal states
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    mobile: "",
    address: "",
    gst_no: "",
    state: "",
    city: "",
    pin_code: ""
  });
  const [customerErrors, setCustomerErrors] = useState({});
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [customerSuccess, setCustomerSuccess] = useState("");

  // Refs for input fields
  const customerRef = useRef(null);
  const dateRef = useRef(null);
  const productSearchRef = useRef(null);
  const sellingRateRef = useRef(null);
  const qtyRef = useRef(null);

  const navigate = useNavigate();

  // Fetch customers and products on component mount
  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  // Fetch customers from API
  const fetchCustomers = async () => {
    try {
      const response = await api.post("/customer_list.php", {});
      if (response.data.head.code === 200) {
        setCustomers(response.data.body);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      const response = await api.post("/product_list.php", {});
      if (response.data.head.code === 200) {
        setProducts(response.data.body);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    }
  };

  // Filter products based on search input
  const getFilteredProducts = () => {
    if (!formData.productSearch) {
      return products.slice(0, 10);
    }

    const searchTerm = formData.productSearch.toLowerCase();
    const filtered = products.filter(product => {
      const hsnCode = product.hsn_code || product.code || "";
      const productName = product.name || product.product_name || "";
      return (
        hsnCode.toLowerCase().includes(searchTerm) ||
        productName.toLowerCase().includes(searchTerm)
      );
    });
    return filtered.slice(0, 10);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'productSearch') {
      setShowProductDropdown(true);
    }
  };

  // Handle product selection from dropdown
  const handleProductSelect = (product) => {
    const productName = product.name || product.product_name || "";
    // const hsnCode = product.hsn_code || product.code || "";
    const productId = product.id; // assume this comes from your product object

    const hsnCode = `PROD${productId.toString().length === 1
      ? "0" + productId
      : productId
      }`;
    console.log("product id ", product.id);
    setFormData(prev => ({
      ...prev,
      productSearch: `${hsnCode} - ${productName}`,
      selectedProduct: {
        hsn_code: hsnCode,
        name: productName,
        id: product.id
      }
    }));
    setShowProductDropdown(false);
    sellingRateRef.current.focus();
  };

  const handleAddItem = () => {
    console.log("from add item", formData.selectedProduct.id);
    if (!formData.selectedProduct || !formData.sellingRate || !formData.qty) {
      alert("Please fill all product fields");
      return;
    }

    const newItem = {
      product_id: formData.selectedProduct.id || '',
      product_code: formData.selectedProduct.hsn_code || '',
      item_name: formData.selectedProduct.name,
      selling_rate: parseFloat(formData.sellingRate) || 0,
      quantity: parseInt(formData.qty) || 1,
      total: (parseFloat(formData.sellingRate) || 0) * (parseInt(formData.qty) || 1)
    };
    console.log("from line 147 ", formData.selectedProduct.id);
    setOrderItems([...orderItems, newItem]);

    console.log("from line 147 ", orderItems);
    // Reset product fields only
    setFormData(prev => ({
      ...prev,
      productSearch: "",
      selectedProduct: null,
      sellingRate: "",
      qty: ""
    }));

    // Focus back on product search for next entry
    productSearchRef.current.focus();
    setShowProductDropdown(false);
  };

  // Enter key navigation handler
  const handleKeyPress = (e, nextFieldRef, action = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action) {
        action();
      } else if (nextFieldRef && nextFieldRef.current) {
        nextFieldRef.current.focus();
      }
    }
  };

  // Special handler for product search
  const handleProductSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filtered = getFilteredProducts();
      if (filtered.length === 1) {
        handleProductSelect(filtered[0]);
      } else if (filtered.length > 0 && formData.productSearch) {
        sellingRateRef.current.focus();
      }
    }
  };

  // Special handler for selling rate field
  const handleSellingRateKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (formData.selectedProduct && formData.sellingRate) {
        qtyRef.current.focus();
      }
    }
  };

  // Special handler for quantity field to add item
  const handleQtyKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleGstChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, gst_percentage: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...orderItems];

    if (field === 'selling_rate') {
      updatedItems[index].selling_rate = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      updatedItems[index].quantity = parseInt(value) || 0;
    } else {
      updatedItems[index][field] = value;
    }

    // Calculate total if selling rate or quantity changes
    if (field === 'selling_rate' || field === 'quantity') {
      updatedItems[index].total = updatedItems[index].selling_rate * updatedItems[index].quantity;
    }

    setOrderItems(updatedItems);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(updatedItems);
  };

  // Customer Modal Functions
  const handleShowCustomerModal = () => {
    setShowCustomerModal(true);
    setCustomerErrors({});
    setCustomerSuccess("");
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    // Reset form
    setNewCustomer({
      name: "",
      mobile: "",
      address: "",
      gst_no: "",
      state: "",
      city: "",
      pin_code: ""
    });
    setCustomerErrors({});
    setCustomerSuccess("");
    setIsAddingCustomer(false);
  };

  // Handle new customer form change
  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (customerErrors[name]) {
      setCustomerErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Validate customer form
  const validateCustomer = () => {
    const errors = {};

    if (!newCustomer.name.trim()) {
      errors.name = "Customer name is required";
    }

    if (!newCustomer.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(newCustomer.mobile)) {
      errors.mobile = "Enter a valid 10-digit mobile number";
    }

    return errors;
  };

  // Handle adding customer via API
  const handleAddCustomer = async () => {
    const validationErrors = validateCustomer();

    if (Object.keys(validationErrors).length > 0) {
      setCustomerErrors(validationErrors);
      return;
    }

    try {
      setIsAddingCustomer(true);
      setCustomerSuccess("");
      setCustomerErrors({});

      // Prepare payload matching customer_add.php API
      const payload = {
        name: newCustomer.name,
        mobile: newCustomer.mobile,
        gst_no: newCustomer.gst_no || "",
        state: newCustomer.state || "",
        city: newCustomer.city || "",
        address: newCustomer.address || "",
        pin_code: newCustomer.pin_code || ""
      };

      // Call the customer_add.php API
      const response = await api.post("/customer_add.php", payload);

      if (response.data.head.code === 200) {
        // Success - add new customer to local state
        const newCustomerObj = {
          id: response.data.body.customer_id,
          name: newCustomer.name,
          mobile: newCustomer.mobile,
          gst_no: newCustomer.gst_no,
          state: newCustomer.state,
          city: newCustomer.city,
          address: newCustomer.address,
          pin_code: newCustomer.pin_code
        };

        // Add to customers list
        setCustomers(prev => [...prev, newCustomerObj]);

        // Auto-select the new customer
        setFormData(prev => ({
          ...prev,
          customer_id: response.data.body.customer_id
        }));

        // Show success message
        setCustomerSuccess(`Customer "${newCustomer.name}" added successfully!`);

        // Reset form and close modal after 2 seconds
        setTimeout(() => {
          handleCloseCustomerModal();
          // Focus back on customer dropdown
          if (customerRef.current) {
            customerRef.current.focus();
          }
        }, 2000);

      } else {
        // Handle API error
        const errorMsg = response.data.head.msg || "Failed to add customer";
        setCustomerErrors({ api: errorMsg });
      }
    } catch (err) {
      console.error("Error adding customer:", err);

      let errorMessage = "Failed to add customer. Please try again.";

      if (err.response) {
        // The request was made and the server responded with a status code
        console.error("Response error:", err.response.data);
        errorMessage = err.response.data?.head?.msg || errorMessage;
      } else if (err.request) {
        // The request was made but no response was received
        console.error("No response received:", err.request);
        errorMessage = "No response from server. Please check your connection.";
      }

      setCustomerErrors({ api: errorMessage });
    } finally {
      setIsAddingCustomer(false);
    }
  };

  // Handle keydown in customer modal
  const handleCustomerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddCustomer();
    }
  };

  const calculateSubTotal = () => {
    return orderItems.reduce((total, item) => total + (item.total || 0), 0);
  };

  const calculateGST = () => {
    const subTotal = calculateSubTotal();
    return (subTotal * (parseFloat(formData.gst_percentage) / 100));
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const gst = calculateGST();
    return subTotal + gst;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_id) newErrors.customer = "Customer is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (orderItems.length === 0) newErrors.items = "At least one item is required";
    return newErrors;
  };

  const formatDateToDMY = (date) => {
    if (!date) return "";
    const [yyyy, mm, dd] = date.split("-");
    return `${dd}-${mm}-${yyyy}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);
    setErrors({});
    setSubmitError("");

    try {
      const payload = {
        customer_id: formData.customer_id,
        order_date: formatDateToDMY(formData.date),
        sub_total: calculateSubTotal().toFixed(2),
        gst_percentage: formData.gst_percentage,
        gst_amount: calculateGST().toFixed(2),
        grand_total: calculateGrandTotal().toFixed(2),
        items: orderItems.map(item => ({
          product_id: item.product_id,
          product_code: item.product_code,
          item_name: item.item_name,
          selling_rate: item.selling_rate.toString(),
          quantity: item.quantity.toString(),
          total: item.total.toFixed(2)
        }))
      };

      console.log("order payload is ", payload);
      /* */
      const response = await api.post("/order_add.php", payload);

      if (response.data.head.code === 200) {
        alert("Order created successfully!");
        navigate("/orderlist");
      } else {
        const errorMsg = response.data.head.msg || "Failed to create order";
        setSubmitError(errorMsg);
      }

    } catch (error) {
      console.error("Error creating order:", error);
      setSubmitError("Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderError = (field) =>
    errors[field] ? <div className="text-danger small mt-1">{errors[field]}</div> : null;

  // Render customer field error
  const renderCustomerError = (field) =>
    customerErrors[field] ? (
      <div className="text-danger small mt-1">{customerErrors[field]}</div>
    ) : null;

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Create Order</h4>
        <Button className="btn-top" onClick={() => navigate(-1)} disabled={loading}>
          Back
        </Button>
      </div>

      {submitError && (
        <Alert variant="danger" className="mb-3">
          {submitError}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>Select Customer</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  ref={customerRef}
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, dateRef)}
                  disabled={loading}
                  isInvalid={!!errors.customer}
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="outline-primary"
                  onClick={handleShowCustomerModal}
                  className="d-flex align-items-center"
                  title="Add New Customer"
                  disabled={loading}
                  type="button"
                >
                  <FaPlus />
                </Button>
              </div>
              {renderError("customer")}
            </Form.Group>
          </Col>

          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                ref={dateRef}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                onKeyPress={(e) => handleKeyPress(e, productSearchRef)}
                disabled={loading}
                isInvalid={!!errors.date}
              />
              {renderError("date")}
            </Form.Group>
          </Col>

          {/* Product Search Row */}
          <Col lg={6} md={6} sm={12}>
            <Form.Group className="position-relative">
              <Form.Label>Search Product *</Form.Label>

              <Form.Control
                ref={productSearchRef}
                name="productSearch"
                type="text"
                value={formData.productSearch}
                onChange={handleChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                onKeyPress={handleProductSearchKeyPress}
                placeholder="Type product code or name..."
                autoComplete="off"
                disabled={loading}
              />

              {showProductDropdown && (
                <div className="custom-dropdown">
                  {getFilteredProducts().map((product, index) => {
                    const productName =
                      product.name || product.product_name || "";
                    const productId = product.id;

                    const hsnCode = `PROD${productId.toString().length === 1
                        ? "0" + productId
                        : productId
                      }`;

                    return (
                      <div
                        key={product.id}
                        className="dropdown-item"
                        onMouseDown={(e) => {
                          e.preventDefault(); // 🔥 IMPORTANT FIX
                          handleProductSelect(product);
                        }}
                      >
                        <div className="fw-bold">
                          {hsnCode} - {productName}
                        </div>
                      </div>
                    );
                  })}

                  {getFilteredProducts().length === 0 && (
                    <div className="dropdown-item text-muted">
                      No products found
                    </div>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>


          <Col lg={3} md={3} sm={6}>
            <Form.Group>
              <Form.Label>Selling Rate (₹) *</Form.Label>
              <Form.Control
                ref={sellingRateRef}
                className="right-align"
                name="sellingRate"
                type="number"
                value={formData.sellingRate}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
                onKeyPress={handleSellingRateKeyPress}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={3} sm={6}>
            <Form.Group>
              <Form.Label>Quantity *</Form.Label>
              <Form.Control
                ref={qtyRef}
                className="right-align"
                name="qty"
                type="number"
                value={formData.qty}
                onChange={handleChange}
                placeholder="0"
                min="1"
                step="0.01"
                onKeyPress={handleQtyKeyPress}
                disabled={loading}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Order Items Table */}
        <Row className="mt-4">
          <Col xs={12}>
            <div className="table-responsive">
              <Table bordered className="purchase-table">
                <thead className="table-dark">
                  <tr>
                    <th width="5%">Sno</th>
                    <th width="25%">Item</th>
                    {/* <th width="15%">HSN Code</th> */}
                    <th width="15%">Selling Rate (₹)</th>
                    <th width="10%">Qty</th>
                    <th width="15%">Total (₹)</th>
                    <th width="10%">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{item.item_name}</td>
                      {/* <td className="text-center">{item.product_code}</td> */}
                      <td>
                        <Form.Control
                          type="number"
                          value={item.selling_rate}
                          className="right-align"
                          onChange={(e) => handleItemChange(index, 'selling_rate', e.target.value)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          disabled={loading}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="0"
                          min="1"
                          className="right-align"
                          disabled={loading}
                        />
                      </td>
                      <td className="text-center">₹{item.total.toFixed(2)}</td>
                      <td className="text-center">
                        <FaTrash
                          className="delete-icon"
                          style={{ cursor: loading ? 'not-allowed' : 'pointer', color: '#dc3545', opacity: loading ? 0.5 : 1 }}
                          onClick={() => !loading && handleDeleteItem(index)}
                          title="Delete Item"
                        />
                      </td>
                    </tr>
                  ))}
                  {orderItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        No items added yet. Search products and press Enter in quantity field to add items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {renderError("items")}

        {/* Totals Section */}
        <Row className="mt-4">
          <Col xs={12} className="d-flex justify-content-end">
            <div className="totals-section">
              <div className="total-row">
                <span className="total-label">Subtotal:</span>
                <span className="total-value">₹{calculateSubTotal().toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span className="total-label">
                  GST (
                  <Form.Control
                    type="number"
                    name="gst_percentage"
                    value={formData.gst_percentage}
                    onChange={handleGstChange}
                    className="gst-input"
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={loading}
                  />
                  %)
                </span>
                <span className="total-value">₹{calculateGST().toFixed(2)}</span>
              </div>
              <div className="total-row grand-total">
                <span className="total-label">Grand Total:</span>
                <span className="total-value">₹{calculateGrandTotal().toFixed(2)}</span>
              </div>
            </div>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button type="submit" className="btn-submit" disabled={loading || orderItems.length === 0}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              "Create Order"
            )}
          </Button>
          <Button type="button" className="btn-top" onClick={() => navigate(-1)} disabled={loading}>
            Cancel
          </Button>
        </div>
      </Form>

      {/* Add Customer Modal */}
      <Modal show={showCustomerModal} onHide={handleCloseCustomerModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customerSuccess && (
            <Alert variant="success" className="mb-3">
              {customerSuccess}
            </Alert>
          )}

          {customerErrors.api && (
            <Alert variant="danger" className="mb-3">
              {customerErrors.api}
            </Alert>
          )}

          <Form onKeyDown={handleCustomerKeyDown}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newCustomer.name}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter customer name"
                    required
                    isInvalid={!!customerErrors.name}
                    autoFocus
                  />
                  {renderCustomerError("name")}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={newCustomer.mobile}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    isInvalid={!!customerErrors.mobile}
                  />
                  {renderCustomerError("mobile")}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={newCustomer.address}
                onChange={handleNewCustomerChange}
                placeholder="Enter address"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={newCustomer.city}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={newCustomer.state}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter state"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pin Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pin_code"
                    value={newCustomer.pin_code}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter pin code"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Number (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="gst_no"
                    value={newCustomer.gst_no}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter GST number"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseCustomerModal}
            disabled={isAddingCustomer}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddCustomer}
            disabled={!newCustomer.name.trim() || !newCustomer.mobile.trim() || isAddingCustomer}
          >
            {isAddingCustomer ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Customer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddOrder;